export const RSVP_AUTOSAVE_DELAY_MS = 5000;

export enum RsvpAutosaveStatus {
  SAVED = "saved",
  PENDING = "pending",
  SAVING = "saving",
  ERROR = "error",
}

export type RsvpAutosaveTask = () => Promise<void>;

type RsvpAutosaveControllerOptions = {
  delayMs?: number;
  onStatusChange: (status: RsvpAutosaveStatus) => void;
};

export type RsvpAutosaveController = {
  cancel: (key: string) => void;
  destroy: () => void;
  flush: () => Promise<void>;
  hasUnsavedChanges: () => boolean;
  queue: (key: string, task: RsvpAutosaveTask) => void;
  waitForActiveSave: () => Promise<void>;
};

export const createRsvpAutosaveController = ({
  delayMs = RSVP_AUTOSAVE_DELAY_MS,
  onStatusChange,
}: RsvpAutosaveControllerOptions): RsvpAutosaveController => {
  const pendingTasks = new Map<string, RsvpAutosaveTask>();
  let activeSave: Promise<void> | null = null;
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  let flushAfterActiveSave = false;
  let destroyed = false;

  const setStatus = (status: RsvpAutosaveStatus) => {
    if (!destroyed) onStatusChange(status);
  };

  const clearDebounceTimer = () => {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = null;
  };

  const flush = async (): Promise<void> => {
    clearDebounceTimer();

    if (activeSave) {
      flushAfterActiveSave = true;
      return activeSave;
    }

    if (pendingTasks.size === 0) return;

    const tasksToSave = new Map(pendingTasks);
    pendingTasks.clear();
    setStatus(RsvpAutosaveStatus.SAVING);

    activeSave = (async () => {
      const taskEntries = Array.from(tasksToSave.entries());
      const results = await Promise.all(
        taskEntries.map(async ([key, task]) => {
          try {
            await task();
            return { key, succeeded: true };
          } catch (_error) {
            return { key, succeeded: false };
          }
        })
      );

      let failed = false;
      results.forEach(({ key, succeeded }) => {
        if (!succeeded) {
          failed = true;
          const task = tasksToSave.get(key);
          if (!pendingTasks.has(key) && task) pendingTasks.set(key, task);
        }
      });

      activeSave = null;

      if (failed) {
        flushAfterActiveSave = false;
        setStatus(RsvpAutosaveStatus.ERROR);
        return;
      }

      if (pendingTasks.size === 0) {
        flushAfterActiveSave = false;
        setStatus(RsvpAutosaveStatus.SAVED);
        return;
      }

      if (flushAfterActiveSave) {
        flushAfterActiveSave = false;
        await flush();
        return;
      }

      setStatus(RsvpAutosaveStatus.PENDING);
    })();

    return activeSave;
  };

  const queue = (key: string, task: RsvpAutosaveTask) => {
    pendingTasks.set(key, task);
    clearDebounceTimer();
    debounceTimer = setTimeout(() => void flush(), delayMs);

    setStatus(
      activeSave ? RsvpAutosaveStatus.SAVING : RsvpAutosaveStatus.PENDING
    );
  };

  const cancel = (key: string) => {
    pendingTasks.delete(key);
    if (pendingTasks.size === 0) {
      clearDebounceTimer();
      if (!activeSave) setStatus(RsvpAutosaveStatus.SAVED);
    }
  };

  const destroy = () => {
    destroyed = true;
    clearDebounceTimer();
    pendingTasks.clear();
  };

  return {
    cancel,
    destroy,
    flush,
    hasUnsavedChanges: () => pendingTasks.size > 0 || activeSave != null,
    queue,
    waitForActiveSave: async () => {
      if (activeSave) await activeSave;
    },
  };
};
